import { AsyncPipe, NgClass } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  EventEmitter,
  inject,
  Output,
  signal,
  ViewChild,
} from '@angular/core';

import { FileManagerComponent } from '@shared/components/file-manager/file-manager.component';
import { FileViewerComponent } from '@shared/components/file-viewer/file-viewer.component';
import { IconComponent } from '@shared/components/icon/icon.component';
import { MessagesConstants } from '@shared/constants/messages.constants';
import { FileMetadata } from '@shared/services/api.service';
import { DocumentType } from '@shared/services/gql.service';
import { OverlayService } from '@shared/services/overlay.service';
import { injectPermissions } from '@shared/store/auth/auth.service';
import { MainQuery } from '@shared/store/main/main.query';

import { DocumentsPageService } from '../../documents/documents-page.service';

// ((fileManager?.fileQuery?.selectAll() | async; as files) && files.length)
@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [FileManagerComponent, FileViewerComponent, IconComponent, AsyncPipe, NgClass],
  // selector: 'aux-invoice-items-no-rows',
  template: `
    <div class="flex h-full flex-col items-center justify-center">
      <aux-icon name="AlertTriangle" class="text-aux-gray-darkest" />
      <span class="text-lg font-bold">This is an empty mapping file &nbsp;</span>
      <span class="text-sm">XLSX EDC Mapping File &nbsp;</span>
      <div
        class="w-full py-2"
        [ngClass]="{
          'invisible h-0 py-0': ((fileManager?.fileQuery?.selectAll() | async)?.length ?? 0) > 0,
        }"
      >
        <aux-file-manager
          #manager
          [fetchFilesOnInit]="false"
          [eager]="false"
          [metadata]="metadata"
          [showSuccessOnUpload]="true"
          [accept]="'.xlsx'"
        />
      </div>
      <div class="mt-3 max-h-60 overflow-auto">
        <aux-file-viewer
          [fileManager]="manager"
          [disableFirstFileMargin]="true"
          [onlyShowUploaded]="false"
        />
      </div>
      @if (errorMessage()) {
        <div class="mt-3 rounded-md bg-aux-error p-5 font-medium text-white">
          {{ errorMessage() }}
        </div>
      }
    </div>
  `,
})
export class EdcMappingNoRowsUploadComponent {
  documentsPageService = inject(DocumentsPageService);
  overlayService = inject(OverlayService);

  protected mainQuery = inject(MainQuery);

  permissions = injectPermissions({
    hasEditEdcMapping: 'PERMISSION_UPLOAD_EDC_MAPPING',
  });

  errorMessage = signal('');
  loading = signal(false);

  disabled = computed(() => !this.permissions().hasEditEdcMapping);
  disabledTooltip = computed(() => {
    if (!this.permissions().hasEditEdcMapping) {
      return MessagesConstants.DO_NOT_HAVE_PERMISSIONS_TO_ACTION;
    }

    return '';
  });

  @Output() closeDialog = new EventEmitter();
  @ViewChild(FileManagerComponent) fileManager: FileManagerComponent | undefined;
  metadata: FileMetadata = {
    documentType: DocumentType.DOCUMENT_EDC_MAPPING,
    trialId: this.mainQuery.getValue().trialKey,
  };

  getFilePath() {
    const trialId = this.mainQuery.getValue().trialKey;

    // TODO - figure out if this is where we want to store the file
    // aws-transfer-family/uploads/edc-documents/<trialId>/
    return `aws-transfer-family/uploads/edc-doucments/${trialId}/mapping-file.xlsx`;
  }

  async onUpload(): Promise<boolean> {
    this.errorMessage.set('');

    if (!(this.fileManager && !this.loading())) {
      return false;
    }

    const files = this.fileManager.fileQuery.getAll();

    if (!files.length) {
      this.errorMessage.set(MessagesConstants.FILE.NEED_UPLOAD_FILE);

      return false;
    }

    if (files.length > 1) {
      this.errorMessage.set(MessagesConstants.FILE.MAX_ONE_FILE);

      return false;
    }

    const file = files[0];

    const key = this.getFilePath();

    this.fileManager.fileStore.update(file.id, {
      ...file,
      bucket_key: key,
    });

    const fileSuccess = await this.fileManager.fileService.uploadFiles({
      metadata: this.metadata,
    });

    if (fileSuccess) {
      this.overlayService.success('EDC mapping file uploaded successfully');
      this.closeDialog.emit();
    } else {
      this.overlayService.error('Error uploading EDC mapping file');
    }

    return fileSuccess;
  }
}
